import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FooterService } from '../services/footer';

// Reproduce today's exact static content so a fresh install (no admin edits
// yet) renders pixel-identical to before the footer became CMS-driven.
// "Services" and "Study Abroad" have no dedicated overview page of their
// own (they're dropdown-only menus in the main nav), so they point at the
// first/most representative sub-page instead. "Europe" and "Ireland" are
// left as '#' — unlike the other six destinations, neither has a page
// built yet, and linking them to an unrelated country page would be
// actively misleading.
const DEFAULT_LINK_COLUMNS = [
  {
    title: 'COMPANY',
    links: [
      { label: 'Home', href: '/' },
      { label: 'About Us', href: '/about-us' },
      { label: 'Services', href: '/services/education-loan' },
      { label: 'Study Abroad', href: '/study-in-uk' },
      { label: 'Finance', href: '/services/education-loan' },
      { label: 'News & Events', href: '/news' },
    ],
  },
  {
    title: 'STUDY DESTINATIONS',
    links: [
      { label: 'United Kingdom (UK)', href: '/study-in-uk' },
      { label: 'United States (USA)', href: '/study-in-usa' },
      { label: 'United Arab Emirates (UAE)', href: '/study-in-uae' },
      { label: 'Australia', href: '/study-in-australia' },
      { label: 'Europe', href: '#' },
      { label: 'Canada', href: '/study-in-canada' },
      { label: 'Ireland', href: '#' },
      { label: 'New Zealand', href: '/study-in-new-zealand' },
    ],
  },
  {
    title: 'EXAM PREPARATIONS',
    links: [
      { label: 'GMAT', href: '/coaching/gmat' },
      { label: 'IELTS', href: '/coaching/ielts' },
      { label: 'GRE', href: '/coaching/gre' },
      { label: 'SAT', href: '/coaching/sat' },
      { label: 'TOEFL', href: '/coaching/toefl' },
      { label: 'PTE', href: '/coaching/pte' },
    ],
  },
  {
    title: 'OTHERS',
    links: [
      { label: 'Terms and Conditions', href: '/terms-and-conditions' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Disclaimer', href: '/disclaimer' },
      { label: 'FAQs', href: '/faqs' },
    ],
  },
];

// Shown under the "Exam Preparations" heading as three side-by-side groups,
// mirroring the header's Coaching dropdown (keep the two in sync).
const COACHING_GROUPS = [
  {
    title: 'COACHING',
    links: [
      { label: 'IELTS', href: '/coaching/ielts' },
      { label: 'PTE', href: '/coaching/pte' },
      { label: 'TOEFL iBT', href: '/coaching/toefl' },
      { label: 'CELPIP', href: '/coaching/celpip' },
      { label: 'DUOLINGO', href: '/coaching/duolingo' },
      { label: 'Inter. Preparatory', href: '/coaching/inter-preparatory' },
    ],
  },
  {
    title: 'COMPETITIVE EXAM PREPARATION',
    links: [
      { label: 'SAT', href: '/coaching/sat' },
      { label: 'GRE', href: '/coaching/gre' },
      { label: 'GMAT', href: '/coaching/gmat' },
    ],
  },
  {
    title: 'LANGUAGE LEARNING',
    links: [
      { label: 'French Language', href: '/coaching/french' },
      { label: 'German Language', href: '/coaching/german' },
      // No /coaching/japanese page yet — plain text until it's built.
      { label: 'Japanese Language', href: '#' },
    ],
  },
];

const isExamPreparationColumn = (col) => /exam\s*preparation/i.test(String(col?.title || ''));

const DEFAULT_CONTACT_INFO = {
  orgName: 'Edugen Study Abroad Consultants',
  delhiOfficeName: 'Delhi Office',
  delhiOfficeAddress: 'C-707, Shiksha Bharti School Road, Near Ramphal Chowk,\nBlock C, Sector 7, Dwarka\nDelhi - 110075',
  gurugramOfficeName: 'Gurugram Office',
  gurugramOfficeAddress: 'Welldone Techpark, 810A, 8th Floor, Badshahpur Sohna Rd Hwy,\nSector 48, Gurugram, Haryana 122001',
  phone: '+9196-5000-5657',
  email: 'info@edugenstudy.com',
  youtubeUrl: 'https://www.youtube.com/@HeyEdugen',
  facebookUrl: 'https://www.facebook.com/edugenstudy',
  instagramUrl: 'https://www.instagram.com/edugenstudy_official/?hl=en ',
  linkedinUrl: 'https://in.linkedin.com/company/edugen-study',
};

// Google Maps search link for a multi-line office address.
const mapsUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(address || '').replace(/\n/g, ', '))}`;

const Footer = () => {
  const [cms, setCms] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await FooterService.getPublicContent();
        if (!cancelled) setCms(data && typeof data === 'object' ? data : {});
      } catch (error) {
        console.error('Failed to load footer content:', error);
        if (!cancelled) setCms({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tagline = cms.newsletter?.tagline || 'Your destiny is in your hands, not where you were born';
  const columns = cms.link_columns?.columns?.length ? cms.link_columns.columns : DEFAULT_LINK_COLUMNS;
  // Only let saved values override the defaults when they're actually filled
  // in — a field left blank in the admin Footer page used to wipe out the
  // default (e.g. a social URL), leaving a dead href="#" link on every page.
  const savedContact = Object.fromEntries(
    Object.entries(cms.contact_info || {}).filter(([, value]) => String(value ?? '').trim() !== '')
  );
  const contact = { ...DEFAULT_CONTACT_INFO, ...savedContact };
  const copyrightText = cms.bottom_bar?.copyrightText || '© 2020 - 2026 Edugen Study. All rights reserved.';
  const maintainedByText = cms.bottom_bar?.maintainedByText || 'Created and Maintained by Edugen Study Abroad Consultants.';

  // Internal paths (including CMS-admin-edited ones) go through React Router
  // so navigating the footer doesn't force a full page reload; external hrefs
  // stay as plain anchors. Entries with no destination yet (blank or "#",
  // e.g. Europe/Ireland before their pages exist) stay visible as plain text
  // instead of a dead "#" link.
  const renderLinkList = (links = []) => (
    <ul className="space-y-2">
      {links.map((link, lIdx) => {
        const href = String(link.href || '').trim();
        const textClassName = "font-['Inter'] font-normal text-[14px] leading-[24px] tracking-normal text-gray-300";
        const linkClassName = `${textClassName} hover:text-orange-400 transition-colors`;
        return (
          <li key={lIdx}>
            {!href || href === '#' ? (
              <span className={textClassName}>{link.label}</span>
            ) : href.startsWith('/') ? (
              <Link to={href} className={linkClassName}>{link.label}</Link>
            ) : (
              <a href={href} className={linkClassName}>{link.label}</a>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <footer className="text-white rounded-[40px] mx-3 sm:mx-6 md:mx-10 lg:mx-16 mb-6 bg-[#1E1E1E] overflow-hidden">
      {/* Newsletter Subscription */}
      <div className="bg-black py-4 px-4 sm:px-6">
        <div className="max-w-[1600px] mx-auto flex flex-col xl:flex-row justify-between items-center gap-4">
          <div className="w-full xl:w-auto flex items-center gap-3">
            <img
              src="/images/logo-white.png"
              alt="Edugen Logo"
              className="h-10 w-auto"
            />
            <p className="text-gray-300 italic">{tagline}</p>
          </div>
          <div className="w-full xl:w-auto flex flex-col sm:flex-row gap-4">
            <div className="relative w-full xl:w-[348px]">
              <input
                type="email"
                placeholder="Enter your email to get the latest news..."
                className="w-full h-11 pl-4 pr-10 rounded-[8px] border border-[#D1D8D5] bg-[#F8FAF9] text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <svg className="w-4 h-4 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <button className="w-[107px] h-[45px] shrink-0 rounded-full bg-gradient-to-r from-[#F68870] to-[#E05C3E] hover:opacity-90 font-medium transition-opacity flex items-center justify-center">
              Subscribe
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6 py-10">
        {/* Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[0.8fr_1.1fr_2.6fr_0.9fr_auto] gap-x-8 gap-y-6 mb-4">
          {columns.map((col, cIdx) => (
            <div key={cIdx} className={isExamPreparationColumn(col) ? 'sm:col-span-2 xl:col-span-1' : undefined}>
              <h2 className={`mb-4 font-['Poppins'] font-medium text-[14px] leading-[24px] tracking-normal uppercase text-[#F47B5F] ${isExamPreparationColumn(col) ? 'text-center' : ''}`}>{col.title}</h2>
              {isExamPreparationColumn(col) ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {COACHING_GROUPS.map((group) => (
                    <div key={group.title}>
                      <h3 className="mb-2 font-['Poppins'] font-medium text-[12px] leading-[18px] tracking-wide uppercase text-white">{group.title}</h3>
                      {renderLinkList(group.links)}
                    </div>
                  ))}
                </div>
              ) : renderLinkList(col.links)}
            </div>
          ))}

          {/* Contact Us */}
          <div>
            <h2 className="mb-4 font-['Poppins'] font-medium text-[14px] leading-[24px] tracking-normal uppercase text-[#F47B5F]">CONTACT US</h2>
            <div className="space-y-4">
              <p className="font-['Poppins'] font-semibold text-[18px] sm:text-[20px] leading-[24px] tracking-normal uppercase text-white xl:whitespace-nowrap">{contact.orgName}</p>

              <div className="flex items-start">
                <svg className="w-5 h-5 text-[#B0DAE1] mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9h.01M9 12h.01M9 15h.01" />
                </svg>
                <a href={mapsUrl(contact.delhiOfficeAddress)} target="_blank" rel="noopener noreferrer" className="group">
                  <p className="text-white text-sm font-bold">{contact.delhiOfficeName}</p>
                  {String(contact.delhiOfficeAddress || '').split('\n').map((line, i) => (
                    <p key={i} className="font-['Inter'] font-normal text-[14px] leading-[24px] tracking-normal text-gray-300 group-hover:text-white transition-colors">{line}</p>
                  ))}
                </a>
              </div>

              <div className="flex items-start">
                <svg className="w-5 h-5 text-[#B0DAE1] mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9h.01M9 12h.01M9 15h.01" />
                </svg>
                <a href={mapsUrl(contact.gurugramOfficeAddress)} target="_blank" rel="noopener noreferrer" className="group">
                  <p className="text-white text-sm font-bold">{contact.gurugramOfficeName}</p>
                  {String(contact.gurugramOfficeAddress || '').split('\n').map((line, i) => (
                    <p key={i} className="font-['Inter'] font-normal text-[14px] leading-[24px] tracking-normal text-gray-300 group-hover:text-white transition-colors">{line}</p>
                  ))}
                </a>
              </div>

              <div className="flex items-center">
                <svg className="w-5 h-5 text-[#B0DAE1] mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <a href={`tel:${String(contact.phone || '').replace(/[^\d+]/g, '')}`} className="font-['Inter'] font-normal text-[14px] leading-[24px] tracking-normal text-gray-300 hover:text-white transition-colors">{contact.phone}</a>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-[#B0DAE1] mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <a href={`mailto:${contact.email}`} className="font-['Inter'] font-normal text-[14px] leading-[24px] tracking-normal text-gray-300 hover:text-white transition-colors">{contact.email}</a>
              </div>

              <div>
                <h3 className="mb-3 font-['Poppins'] font-medium text-[14px] leading-[24px] tracking-normal uppercase text-[#F47B5F]">JOIN US</h3>
                <div className="flex space-x-3">
                  <a href={contact.youtubeUrl || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center hover:opacity-80 transition-opacity">
                    <img src="/images/icons/Buttons-Social-Link-(3).png" alt="YouTube" className="w-full h-full object-contain" />
                  </a>
                  <a href={contact.facebookUrl || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center hover:opacity-80 transition-opacity">
                    <img src="/images/icons/Buttons-Social-Link.png" alt="Facebook" className="w-full h-full object-contain" />
                  </a>
                  <a href={contact.instagramUrl || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center hover:opacity-80 transition-opacity">
                    <img src="/images/icons/Buttons-Social-Link-(1).png" alt="Instagram" className="w-full h-full object-contain" />
                  </a>
                  <a href={contact.linkedinUrl || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center hover:opacity-80 transition-opacity">
                    <img src="/images/icons/Buttons-Social-Link-(2).png" alt="LinkedIn" className="w-full h-full object-contain" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/10 pt-6 mt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm mb-4 md:mb-0">{copyrightText}</p>
          <p className="text-gray-400 text-sm">{maintainedByText}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
